import React, { useState, useEffect } from 'react';

import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { get } from 'lodash';

import {
  Box,
  Grid,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import DoneIcon from '@mui/icons-material/Done';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';

import MyButton from '@common/components/Button/MyButton';
import SelectSourceCheckbox from '@common/components/MyCheckbox/MyCheckbox';
import TextArea from '@common/components/TextArea/TextArea';
import GroupedParameters from '@common/components/GroupedParameters/GroupedParameters';

import {
  isModalCalcOrSum,
  createPathNameForCheckbox,
  isNodeList,
  isNodeObj,
  isListSourceNotSelected,
  isArrayWithSelectedSource,
  isFieldActualPlaceholder,
  getShowStateForArrayWithSource,
  getShowStateForList,
  getShowStateForNodeObj,
  getValidPropKeyForNodeObj,
  getParentPathForNodeObj,
  getModalAction,
  isShowModalCalcAction,
  isShowModalSumAction,
} from '@common/helpers/placeholderModalHelpers';
import { INIT_PLACEHOLDER_MODAL_STATE, MODAL_ACTIONS } from '@common/constants';

const PlaceholderModal = ({
  documentJson,
  showModal,
  setShowModal,
  changeTextFunc,
  currentValue = '',
  currentFormula,
  onCalcSave = () => {},
  setProp,
}) => {
  const [checkedField, setCheckedField] = useState();
  const [parent, setParent] = useState();
  const [calcFormula, setCalcFormula] = useState(
    currentFormula ? currentFormula : ''
  );

  const { t } = useTranslation();

  useEffect(() => {
    isModalCalcOrSum(showModal)
      ? setCheckedField([])
      : setCheckedField(currentValue);
  }, [showModal, currentValue]);

  const getDocTypeRecursive = (providedDocument, propKey, modalShowState) => {
    let currentField = get(providedDocument, propKey);

    if (isNodeList(currentField, modalShowState, propKey)) {
      // render a list with it's node
      return (
        <GroupedParameters>
          <Typography variant='subtitle2' sx={{ mb: '4px' }}>
            {`${t('this_is_list')} ${propKey}`}
          </Typography>
          <SelectSourceCheckbox
            label={t('column_index')}
            name={'inc @index'}
            parent={''}
            checked={checkedField === 'inc @index'}
            onChange={changeSelected}
          />
          {getDocumentFieldsRecursive(
            currentField[0],
            getShowStateForList(modalShowState)
          )}
        </GroupedParameters>
      );
    }

    if (isNodeObj(currentField, modalShowState)) {
      let validPropKey = getValidPropKeyForNodeObj(propKey);
      let parentPath = getParentPathForNodeObj({
        propKey,
        modalShowState,
      });

      // render object with it's node
      return (
        <GroupedParameters>
          <Typography variant='subtitle2' sx={{ mb: '4px' }}>{`${t(
            'this_is_obj'
          )} ${parentPath}${validPropKey}`}</Typography>
          {getDocumentFieldsRecursive(
            currentField,
            getShowStateForNodeObj({
              modalShowState,
              propKey,
            })
          )}
        </GroupedParameters>
      );
    }

    if (isArrayWithSelectedSource(currentField, modalShowState, propKey)) {
      // render nested list with it's node
      return (
        <Box sx={{ m: 1 }}>
          {getDocumentFieldsRecursive(
            currentField,
            getShowStateForArrayWithSource({
              modalShowState,
              propKey,
            })
          )}
        </Box>
      );
    }

    if (isFieldActualPlaceholder(currentField, modalShowState)) {
      const checkboxFieldName = createPathNameForCheckbox(
        modalShowState,
        propKey
      );

      // render checkbox for selection of placeholder
      return (
        <SelectSourceCheckbox
          label={propKey}
          name={checkboxFieldName}
          parent={modalShowState.parent}
          checked={checkedField === checkboxFieldName}
          onChange={changeSelected}
        />
      );
    }

    return <div />;
  };

  const getDocumentFieldsRecursive = (
    providedDocument = {},
    modalShowState
  ) => {
    let objKeys = Object.keys(providedDocument);
    if (modalShowState.show) {
      if (isListSourceNotSelected(objKeys, modalShowState)) {
        return <Box>{t('list_is_not_found')}</Box>;
      }

      return objKeys.map((propKey, index) => {
        return (
          <Box key={index}>
            {getDocTypeRecursive(providedDocument, propKey, modalShowState)}
          </Box>
        );
      });
    }
  };

  const changeSelected = (data) => {
    let newValue = data.name === checkedField ? '' : data.name;

    setCheckedField(newValue);
    setParent(data.parent);

    if (isShowModalCalcAction(showModal)) {
      setCalcFormula((prevState) => `${prevState} ${newValue}`);
    }

    if (isShowModalSumAction(showModal)) {
      let nameArr = get(data, 'name').split('.');
      let newValue = nameArr[1];
      setCalcFormula((prevState) => `${prevState} ${newValue}`);
    }
  };

  const onOkClicked = () => {
    const modalAction = getModalAction(showModal);
    switch (modalAction) {
      case MODAL_ACTIONS.CALC:
        changeTextFunc(calcFormula);
        onCalcSave(calcFormula);
        setCheckedField('');
        break;
      case MODAL_ACTIONS.SOURCE:
        changeTextFunc(checkedField);
        onCalcSave('');
        setCalcFormula('');
        //setting data-th-if for list
        if (/\[[^\]]*\]/.test(parent)) {
          setProp((props) => {
            props.ifAtt = parent.replace(/\[[^\]]*\]/g, '');
          });
        } else {
          setProp((props) => {
            props.ifAtt = '';
          });
        }
        break;
      case MODAL_ACTIONS.SUM:
        changeTextFunc(calcFormula);
        onCalcSave(calcFormula);
        setCheckedField('');
        break;
      default:
    }

    setShowModal(INIT_PLACEHOLDER_MODAL_STATE);
  };

  const onCancel = () => {
    const modalAction = getModalAction(showModal);
    switch (modalAction) {
      case MODAL_ACTIONS.CALC:
        setCheckedField([]);
        setCalcFormula(currentFormula);
        break;
      case MODAL_ACTIONS.SOURCE:
        setCheckedField(currentValue);
        break;
      case MODAL_ACTIONS.SUM:
        let splitValue = currentValue.split('{').pop().split('}')[0];
        setCalcFormula(splitValue);
        setCheckedField([]);
        break;
      default:
    }
    setShowModal(INIT_PLACEHOLDER_MODAL_STATE);
  };

  const onTextAriaChange = (event) => {
    setCalcFormula(event.target.value);
  };

  const onReset = () => {
    setCalcFormula('');
    setCheckedField([]);
  };

  return (
    <Dialog fullWidth maxWidth={'sm'} open={showModal.show}>
      <DialogTitle>{t('modal_header')}</DialogTitle>
      <DialogContent>
        {isModalCalcOrSum(showModal) ? (
          <Box>
            <Grid container spacing={2} sx={{ flexGrow: 1 }}>
              <Grid size={6}>
                {getDocumentFieldsRecursive(documentJson, showModal)}
              </Grid>
              <Grid size={6}>
                <Box sx={{ pt: 1 }}>
                  <TextArea
                    placeholder={t('add_calculation')}
                    value={calcFormula}
                    onChange={onTextAriaChange}
                    rows={6}
                  />
                  <MyButton
                    sx={{ marginTop: '5px' }}
                    name='clear'
                    onClick={onReset}
                  >
                    {t('clear')}
                  </MyButton>
                </Box>
              </Grid>
            </Grid>
          </Box>
        ) : (
          getDocumentFieldsRecursive(documentJson, showModal)
        )}
      </DialogContent>
      <DialogActions>
        <MyButton
          startIcon={<HighlightOffIcon />}
          onClick={onCancel}
          color='error'
        >
          {t('cancel')}
        </MyButton>
        <MyButton
          startIcon={<DoneIcon />}
          color='primary'
          onClick={onOkClicked}
        >
          {t('ok')}
        </MyButton>
      </DialogActions>
    </Dialog>
  );
};

PlaceholderModal.propTypes = {
  documentJson: PropTypes.object,
  showModal: PropTypes.shape({
    show: PropTypes.bool,
    listSource: PropTypes.string,
    parent: PropTypes.string,
    showRootFields: PropTypes.bool,
    action: PropTypes.string,
  }),
  setShowModal: PropTypes.func,
  changeTextFunc: PropTypes.func,
  currentValue: PropTypes.string,
  currentFormula: PropTypes.string,
  onCalcSave: PropTypes.func,
  setProp: PropTypes.func,
};

export default PlaceholderModal;
